import { GoogleAdUnit } from '@/components/@google/GoogleAdUnit';
import CompanyContact from '@/components/company/CompanyContact';
import CompanyFAQs from '@/components/company/CompanyFAQs';
import CompanyFeatures from '@/components/company/CompanyFeatures';
import CompanyHero from '@/components/company/CompanyHero';
import CompanyOffers from '@/components/company/CompanyOffers';
import CompanyPolicy from '@/components/company/CompanyPolicy';
import CompanyRating from '@/components/company/CompanyRating';
import CompanyRelated from '@/components/company/CompanyRelated';
import Breadcrumbs from '@/components/ui/Breadcrumbs';
import VideoPlayer from '@/components/ui/VideoPlayer';
import VideoReview from '@/components/ui/VideoReview';
import { project } from '@/lib/project';
import {
	getAllCompaniesSlugs,
	getCompanyFAQ,
	getCompanyPolicies,
} from '@/lib/repository';
import { TravelAPIClient } from '@/lib/travel-api-client';
import { replaceDuplicates, sanitizeArticle } from '@/lib/utils';
import { some } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { FAQPageJsonLd, NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { MdLink } from 'react-icons/md';
import { getCanonicalUrl, i18nRoute } from 'routes';
import { ArticleIcon } from '../help-center/[companyId]';

const WordCloudComponent = dynamic(
	() =>
		import('@/components/ui/Wordcloud').then((mod) => mod.WordCloudComponent),
	{ ssr: false }
);

const HeaderMobileMenu = dynamic(
	() => import('@/components/ui/HeaderMobileMenu'),
	{ ssr: false }
);

export default function Page({
	locale,
	canonicalURL,
	company,
	offers,
	faqs,
	policies,
	namespace,
	articles,
	reviewsData,
	videos,
	reviewsSentimentVideo,
	reviewsVideos,
	relatedCompanies,
}) {
	const { t } = useTranslation([
		namespace,
		'common',
		'companies',
		'help_center',
	]);

	const pages = [
		{
			name: t('common:links.home.title'),
			href: i18nRoute('/', locale),
			locale,
		},
		{
			name: t(`common:links.companies.title`),
			href: i18nRoute(`/companies`, locale),
			current: false,
			locale,
		},
		{
			name: company.name,
			href: i18nRoute(`/companies/[companyId]`, locale, {
				companyId: company.id,
			}),
			current: true,
			locale: locale,
		},
	];

	const seoTitle = replaceDuplicates(
		t(`title`, {
			company: company.name,
			transport_type: company.transportType,
		})
	);
	const seoDescription = replaceDuplicates(
		t(`description`, {
			company: company.name,
			transport_type: company.transportType,
		})
	);
	const seoKeywords = t(`keywords`, {
		company: company.name,
		transport_type: company.transportType,
	});

	const pageH1 = replaceDuplicates(t('h1', { company: company.name }));
	const pageH2 = replaceDuplicates(t('h2', { company: company.name }));

	const hasArticles = articles && articles.length > 0;
	const hasRoutes = offers && offers.length > 0;
	const hasFAQs = faqs && faqs.length > 0;
	const hasVideos = videos && videos.length > 0;
	const hasRating =
		company.aggregateRating !== null &&
		company.aggregateRating !== undefined &&
		company.aggregateRating.ratingValue > 0;
	const hasSentiment = reviewsData && reviewsData.summary;
	const hasReviewArticle = reviewsData && reviewsData.article;
	const hasReviewsVideos = reviewsVideos && reviewsVideos.length > 0;
	const hasWordCloud = reviewsData && reviewsData.wordCloud;
	const hasPolicies = policies && policies.length > 0;
	const hasContact = company.phone || company.address || company.website;
	const hasFeatures = company.features && some(company.features);

	const navigation = [];
	if (hasRoutes) {
		navigation.push({
			name: t('companies:navigation.routes'),
			href: '#routes',
		});
	}
	if (hasFeatures) {
		navigation.push({
			name: t('companies:navigation.features'),
			href: '#features',
		});
	}
	if (hasVideos) {
		navigation.push({
			name: t('companies:navigation.videos'),
			href: '#videos',
		});
	}
	if (hasArticles) {
		navigation.push({
			name: t('companies:navigation.help'),
			href: '#help',
		});
	}
	if (hasRating) {
		navigation.push({
			name: t('companies:navigation.reviews'),
			href: '#rating',
		});
	}
	if (hasFAQs) {
		navigation.push({
			name: t('companies:navigation.faq'),
			href: '#faq',
		});
	}
	if (hasContact) {
		navigation.push({
			name: t('companies:navigation.contact'),
			href: '#contact',
		});
	}

	return (
		<>
			<NextSeo
				title={seoTitle}
				description={seoDescription}
				canonical={canonicalURL}
				openGraph={{
					url: canonicalURL,
					images: [
						{
							url: `https://${project.domain}/images/busbuster-share.png`,
							width: 1200,
							height: 630,
							alt: company.name,
						},
					],
				}}
				additionalMetaTags={[{ name: 'keywords', content: seoKeywords }]}
			/>

			{hasFAQs && (
				<FAQPageJsonLd
					mainEntity={faqs.map((faq) => ({
						questionName: faq.question,
						acceptedAnswerText: faq.answer,
					}))}
				/>
			)}
			<header className="fixed inset-x-0 top-0 z-40 flex items-center justify-end bg-transparent px-4 py-2 text-gray-900 md:hidden">
				<HeaderMobileMenu className={'bg-white'} useNativeNavigation />
			</header>
			<main className="md:pt-16! pt-0!">
				<CompanyHero company={company} title={pageH1} subtitle={pageH2} />
				<div className="mx-auto max-w-7xl space-y-2 px-4 md:space-y-4 md:py-4 lg:px-8">
					<GoogleAdUnit
						slotId={project.adSense.company.hero}
						className="h-[280px] w-[336px] md:h-[250px] md:w-full lg:h-[250px] lg:w-full"
					/>
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
						<div className="col-span-1 space-y-2 md:space-y-4 lg:col-span-2 lg:space-y-8">
							{navigation.length > 0 && (
								<nav className="py-1">
									<div className="scrollbar-hide flex items-center gap-x-2 overflow-x-auto py-1">
										{navigation.map((nav) => (
											<a href={nav.href} key={nav.href}>
												<span className="whitespace-nowrap rounded-full bg-blue-50 px-3 py-1.5 text-base font-medium text-blue-700 hover:bg-blue-200">
													{nav.name}
												</span>
											</a>
										))}
									</div>
								</nav>
							)}
							{hasRoutes && <CompanyOffers company={company} offers={offers} />}
							{hasRoutes && offers.length >= 10 && (
								<GoogleAdUnit
									className="h-[280px] w-[336px] md:h-[90px] md:w-[728px]"
									slotId={project.adSense.company.routes}
								/>
							)}
							{hasFeatures && (
								<>
									<CompanyFeatures company={company} />
									<GoogleAdUnit
										className="h-[280px] w-[336px] md:h-[90px] md:w-[728px]"
										slotId={project.adSense.company.features}
									/>
								</>
							)}

							{hasVideos && (
								<section id="videos">
									<h2 className="mb-4 text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">
										{company.name} Videos
									</h2>
									<div className="scrollbar-hide relative flex w-full snap-x snap-mandatory gap-4 overflow-x-auto px-1 pb-1 pt-1">
										{videos.map((video) => (
											<div className="flex-1" key={video.id}>
												<VideoPlayer
													className={''}
													videoClassName={''}
													key={video.id}
													videoId={video.id}
													videoTitle={video.title}
													enableAds
													showPlayButton
												/>
											</div>
										))}
									</div>
									{/* <VideoGallery videos={videos} /> */}
								</section>
							)}

							{hasArticles && (
								<>
									<section id="help">
										<Link
											className="mb-4 flex items-center gap-2 hover:underline"
											href={i18nRoute(`/help-center/[companyId]`, locale, {
												companyId: company.id,
											})}
										>
											<h2 className="text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">
												{t('help_center:company.h1', {
													companyName: company.name,
												})}
											</h2>
											<MdLink className="size-6 text-blue-600" />
										</Link>
										<p className="mb-4 text-base text-gray-600">
											{t('help_center:company.description', {
												companyName: company.name,
												serviceNames: articles
													.map((article) => article.title)
													.slice(0, 3)
													.join(', '),
											})}
										</p>
										<ul className="space-y-4">
											{articles.map((article) => (
												<li
													key={article.companyTitle}
													className="relative flex items-start gap-4 rounded-xl bg-white p-4 shadow-sm hover:cursor-pointer hover:ring-1 hover:ring-blue-600"
												>
													<ArticleIcon
														title={article.companyTitle}
														aria-hidden="true"
														className="size-8 shrink-0 text-blue-600 lg:-mt-0.5"
													/>
													<div className="flex-1 space-y-2">
														<Link
															prefetch={false}
															locale={false}
															href={article.href}
															className="inline-block before:absolute before:inset-0 before:content-[]"
														>
															<h2 className="text-balance text-base font-semibold capitalize text-gray-900 lg:text-lg">
																{article.companyTitle}
															</h2>
														</Link>
														<p className="line-clamp-3 text-gray-600">
															{article.excerpt}...
														</p>
													</div>
												</li>
											))}
										</ul>
									</section>
									<GoogleAdUnit
										className="h-[280px] w-[336px] md:h-[90px] md:w-[728px]"
										slotId={project.adSense.company.help_center}
									/>
								</>
							)}
							{(hasRating ||
								reviewsSentimentVideo ||
								hasSentiment ||
								hasReviewsVideos) && (
								<>
									<section id="sentiment" className="space-y-8 py-8">
										<div className="flex flex-wrap justify-between gap-8 lg:gap-12">
											{hasRating && (
												<div className="w-full md:w-[40%]">
													<CompanyRating company={company} />
												</div>
											)}
											{reviewsSentimentVideo && (
												<div className="flex-1">
													<VideoPlayer
														titleClassName={
															'!text-lg !font-bold !tracking-tight !text-slate-900'
														}
														key={reviewsSentimentVideo.id}
														videoId={reviewsSentimentVideo.id}
														videoTitle={reviewsSentimentVideo.title}
														enableAds
													/>
												</div>
											)}
											{hasSentiment && (
												<div className="w-full">
													<p className="text-base text-gray-600">
														{reviewsData.summary}
													</p>
												</div>
											)}
											{hasReviewsVideos && (
												// create a new section for reviews videos with aspect ration 9:16 where the video on right is little over the previous video. Each video has a transformation in angle but they keep vertical, on hover the video is transformed to a different degree and the video is played
												<div className="w-full">
													<div className="relative flex h-full w-full transform gap-1 overflow-x-auto lg:gap-0 lg:overflow-x-visible">
														{reviewsVideos.map((video) => (
															<VideoReview key={video.id} video={video} />
														))}
													</div>
												</div>
											)}
										</div>
									</section>
									<GoogleAdUnit
										className="h-[280px] w-[336px] md:h-[90px] md:w-[728px]"
										slotId={project.adSense.company.rating}
									/>
								</>
							)}

							{hasReviewArticle && (
								<>
									<section
										id="travellers-opinion"
										className="prose"
										dangerouslySetInnerHTML={{ __html: reviewsData.article }}
									/>
									<GoogleAdUnit
										className="h-[280px] w-[336px] md:h-[90px] md:w-[728px]"
										slotId={project.adSense.company.sentiment}
									/>
								</>
							)}
							{hasWordCloud && (
								<>
									<section id="wordcloud" className="pt-8">
										<h2 className="mb-4 text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl">
											{company.name} Wordcloud
										</h2>
										<WordCloudComponent words={reviewsData.wordCloud} />
									</section>
								</>
							)}

							{hasFAQs && (
								<>
									<CompanyFAQs company={company} faqs={faqs} />
									<GoogleAdUnit
										className="h-[280px] w-[336px] md:h-[90px] md:w-[728px]"
										slotId={project.adSense.company.faqs}
									/>
								</>
							)}

							{hasPolicies && (
								<>
									{policies.map((policy) => (
										<CompanyPolicy
											key={policy.topic}
											type={policy.topic}
											policy={policy.content}
										/>
									))}
									<GoogleAdUnit
										className="h-[280px] w-[336px] md:h-[90px] md:w-[728px]"
										slotId={project.adSense.company.policies}
									/>
								</>
							)}

							{hasContact && <CompanyContact company={company} />}

							{relatedCompanies && relatedCompanies.length > 0 && (
								<CompanyRelated companies={relatedCompanies} />
							)}
						</div>

						<div className="col-span-1">
							<GoogleAdUnit
								containerClassName={'sticky top-20'}
								slotId={project.adSense.company.sidebar}
								className="ml-auto mr-0 lg:h-[600px] lg:w-[300px] xl:h-[600px]"
							/>
						</div>
					</div>
				</div>
			</main>
			<Breadcrumbs pages={pages} />
		</>
	);
}

export async function getStaticPaths({ locales }) {
	const locale = project.defaultLocale;
	const slugs = await getAllCompaniesSlugs({ locale });
	// .filter(
	// 	(slug) => slug === 'flixbus' || slug === 'greyhound' || slug === 'amtrak'
	// );

	const paths = slugs.map((slug) => ({
		params: {
			companyId: slug,
		},
		locale: locale,
	}));

	return {
		paths: paths,
		fallback: 'blocking',
	};
}

export async function getStaticProps({ params, locale }) {
	if (locale === 'default') {
		return {
			notFound: true,
		};
	}

	const travelAPIClient = new TravelAPIClient();
	const fullLocale =
		locale.length === 2 ? `${locale}-${project.regions[locale]}` : locale;
	const currency = project.currencies[locale] || project.defaultCurrency;

	const [company, offers, articles, relatedCompanies, reviewsData, allVideos] =
		await Promise.all([
			travelAPIClient.getCompany({ companyId: params.companyId, locale }),
			travelAPIClient.getCompanyOffers({
				companyId: params.companyId,
				locale: fullLocale,
				currency,
			}),
			travelAPIClient.getCompanyContents({
				companyId: params.companyId,
				locale: locale === 'en' ? 'en-US' : locale,
				cache: Date.now(),
			}),
			travelAPIClient.getFeaturedCompanies({
				locale,
				region: project.regions[locale],
			}),
			travelAPIClient.getCompanyReviewsData({
				companyId: params.companyId,
				locale,
			}),
			travelAPIClient.searchVideos({
				tags: [params.companyId],
				limit: 50,
			}),
		]);

	if (!company) {
		return {
			notFound: true,
		};
	}

	delete company.topRoutes;

	const companyArticles = articles.map((a) => {
		const item = sanitizeArticle({ company, article: a, locale });
		return {
			companyTitle: item.companyTitle,
			href: item.href,
			excerpt: item.excerpt,
			title: item.title,
		};
	});

	const data = {
		company: company,
		offers: offers.map((offer) => ({
			departureDateTime: offer.departureDateTime,
			arrivalDateTime: offer.arrivalDateTime,
			price: offer.price,
			duration: offer.duration,
			id: offer.id,
			origin: {
				id: offer.origin.id,
				slug: offer.origin.slug,
				shortName: offer.origin.shortName,
			},
			destination: {
				id: offer.destination.id,
				slug: offer.destination.slug,
				shortName: offer.destination.shortName,
			},
		})),
		articles: companyArticles,
		relatedCompanies: relatedCompanies
			? relatedCompanies
					.filter((r) => r.id !== company.id)
					.map((r) => ({
						id: r.id,
						name: r.name,
						transportType: r.transportType,
					}))
			: null,
		reviewsData: reviewsData,
		faqs: null,
		policies: null,
		schemas: [],
		videos: [],
	};

	if (!data.articles || data.articles.length === 0) {
		const [faqs, policies] = await Promise.all([
			getCompanyFAQ(params.companyId, locale),
			getCompanyPolicies(params.companyId, locale),
		]);

		data.faqs = faqs || null;
		data.policies = policies || null;
	}

	const canonicalURL =
		data.company.transportType === 'train'
			? `https://${project.domain}/${locale}/companies/${data.company.id}`
			: getCanonicalUrl('/companies/[companyId]', locale, {
					companyId: data.company.id,
			  });

	allVideos.forEach((video) => {
		try {
			video.title = video.translations[locale].title;
			video.description = video.translations[locale].description;
			delete video.translations;
		} catch (error) {}
	});

	data.reviewsSentimentVideo =
		allVideos.find(
			(video) =>
				video.tags.length &&
				video.tags.includes('shorts') &&
				video.tags.includes('sentiment') &&
				video.tags.includes(data.company.id)
		) || null;

	data.reviewsVideos = allVideos
		.filter((video) => {
			return (
				video.tags.length &&
				video.tags.includes('shorts') &&
				video.tags.includes('reviews') &&
				video.tags.includes(data.company.id)
			);
		})
		.slice(0, 5);

	const longVideos = allVideos
		.filter((video) => video.tags.length && !video.tags.includes('shorts'))
		.sort((a, b) => {
			return new Date(b.publishedAt) - new Date(a.publishedAt);
		});

	data.videos = longVideos.filter(
		(video) =>
			video.tags.includes('company') &&
			video.tags.includes(data.company.id) &&
			(video.tags.length === 2
				? true
				: video.tags.includes(fullLocale.toLowerCase()))
	);

	data.videos.forEach((video, index) => {
		if (index === 0) {
			video.isMain = true;
		}
		const videoSchema = {
			'@context': 'https://schema.org',
			'@type': 'VideoObject',
			name: video.title,
			description: video.description,
			thumbnailUrl: video.thumbnail,
			uploadDate: video.publishedAt,
			contentUrl: `https://www.youtube.com/watch?v=${video.id}`,
			duration: video.duration,
			embedUrl: `https://www.youtube.com/embed/${video.id}`,
			potentialAction: {
				'@type': 'SeekToAction',
				target: `https://youtu.be/${video.id}?t={seek_to_second_number}`,
				'startOffset-input': 'required name=seek_to_second_number',
			},
		};
		data.schemas.push(videoSchema);
	});

	const organizationSchema = {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		name: data.company.name,
		url: canonicalURL,
	};

	if (data.company.phone && data.company.address) {
		organizationSchema.address = data.company.address;
		organizationSchema.telephone = data.company.phone;
	}

	const hasRating =
		data.company.aggregateRating !== null &&
		data.company.aggregateRating !== undefined &&
		data.company.aggregateRating.ratingValue > 0 &&
		data.company.aggregateRating.reviewCount > 0;

	if (hasRating) {
		organizationSchema.aggregateRating = {
			'@type': 'AggregateRating',
			ratingValue: data.company.aggregateRating.ratingValue,
			bestRating: 5,
			worstRating: 1,
			ratingCount: data.company.aggregateRating.reviewCount,
		};
	}
	data.schemas.push(organizationSchema);

	const namespace = `${company.transportType}_company`;

	return {
		props: {
			headerProps: {
				useIconAsLogo: true,
				className: 'hidden md:block',
			},
			canonicalURL,
			locale,
			namespace,
			...data,
			...(await serverSideTranslations(locale, [
				namespace,
				'common',
				'companies',
				'help_center',
			])),
		},
	};
}
